import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Classic Pokémon GBA-style iris transition: a black circle that closes
 * over the screen when the route changes, then opens up to reveal the
 * new page. Renders a fixed overlay above all content.
 *
 * Implementation notes:
 *  - Uses CSS clip-path circle() for hardware-accelerated animation.
 *  - Triggers only on student routes via the parent component.
 *  - Auto-disables itself on prefers-reduced-motion.
 */
export const IrisTransition = ({ scope = '/student' }) => {
  const location = useLocation();
  const [phase, setPhase] = useState('idle'); // 'idle' | 'closing' | 'open'
  const prevPathRef = useRef(location.pathname);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      prevPathRef.current = location.pathname;
      return;
    }
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    // Only animate within the scope (e.g. /student/*)
    if (!location.pathname.startsWith(scope)) return;

    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    // Close iris → wait → open iris
    setPhase('closing');
    const t1 = setTimeout(() => setPhase('open'), 380);
    const t2 = setTimeout(() => setPhase('idle'), 850);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [location.pathname, scope]);

  if (phase === 'idle') return null;

  // clip-path goes from 150% → 0% (closing) then 0% → 150% (open)
  const clip = phase === 'closing'
    ? 'circle(0% at 50% 50%)'
    : 'circle(150% at 50% 50%)';

  return (
    <div
      data-testid="iris-transition"
      className="fixed inset-0 z-[9999] bg-black pointer-events-none"
      style={{
        clipPath: clip,
        WebkitClipPath: clip,
        transition: 'clip-path 380ms ease-in-out, -webkit-clip-path 380ms ease-in-out',
      }}
    />
  );
};

export default IrisTransition;
